import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Sparkles, TrendingUp, TrendingDown, Star, ArrowUpRight, Loader2 } from "lucide-react";

export default function WeekendRecap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/weekend-recap");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="border border-slate-800 rounded-xl p-6 flex items-center gap-3 text-neutral-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Preparing your recap…
      </div>
    );
  }
  if (!data) return null;

  const positive = data.clicks_delta_pct >= 0;

  return (
    <div data-testid="weekend-recap" className="border border-sky-500/25 rounded-xl p-6 bg-gradient-to-br from-sky-500/8 via-slate-900/60 to-transparent">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> {data.greeting}
          </p>
          <h3 className="font-serif text-2xl text-sky-100 mt-1 leading-tight">
            {data.articles_published > 0 ? (
              <>You shipped <span className="text-sky-400">{data.articles_published}</span> article{data.articles_published === 1 ? "" : "s"} this week.</>
            ) : (
              <>Quiet week — no new articles published.</>
            )}
          </h3>
        </div>
        <Link
          to="/app/refresh-radar"
          data-testid="recap-open-radar"
          className="text-xs px-3 py-1.5 border border-sky-400 text-sky-300 hover:bg-sky-400 hover:text-slate-950 rounded flex items-center gap-1 transition"
        >
          Radar <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Clicks this week */}
        <div className="border border-slate-800 rounded-lg p-4 bg-slate-950/40">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Clicks · 7d</p>
          <p className="font-serif text-3xl font-numeric text-sky-100 mt-1">{data.clicks_this_week}</p>
          <div className={`mt-1 flex items-center gap-1 text-[11px] font-mono ${positive ? "text-emerald-400" : "text-red-400"}`}>
            {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{positive ? "+" : ""}{data.clicks_delta_pct}% vs prior week</span>
          </div>
          <p className="text-[10px] font-mono text-slate-600 mt-0.5">prior: {data.clicks_prior_week}</p>
        </div>

        {/* Portfolio commission */}
        <div className="border border-slate-800 rounded-lg p-4 bg-slate-950/40">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Portfolio commission</p>
          <p className="font-serif text-3xl font-numeric text-sky-300 mt-1">${data.portfolio_commission.toFixed(2)}</p>
          <p className="text-[10px] font-mono text-slate-600 mt-1.5">total across all accounts</p>
        </div>

        {/* Top article */}
        <div className="border border-slate-800 rounded-lg p-4 bg-slate-950/40">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
            <Star className="w-3 h-3 text-sky-400" /> Top this week
          </p>
          {data.top_article ? (
            <>
              <p className="text-sm text-sky-100 mt-1.5 leading-snug line-clamp-2 font-serif">{data.top_article.title}</p>
              <p className="text-[11px] font-mono text-neutral-500 mt-1">
                {data.top_article.clicks_this_week} clicks · <span className="text-sky-400">{data.top_article.network_key}</span>
              </p>
            </>
          ) : (
            <p className="text-sm text-neutral-500 mt-2">No clicks tracked yet this week. Share your articles to build momentum.</p>
          )}
        </div>
      </div>
    </div>
  );
}
